import "bootstrap-sass";
import Cookies from "js-cookie";
import pluralize from "pluralize";
import React from "react";
import ReactDOM from "react-dom";

import debounce from "lodash.debounce";

import { gaEventNoHit, trackClick, trackGoal } from "../shared/helpers/gtm_tracking";
import { pushGa4Event } from "../shared/helpers/ga4_helper";
import { getParamByName, venueParamsToUrl } from "../services/URLService";
import { ISO_DATE_FORMAT, UK_DATE_FORMAT } from "../const/DateFormatConsts";
import { json } from "../shared/fetcher";
import { regularPickerOptions } from "../utils/DatePickerSupport";
import dayjs from "../shared/helpers/dayjs";
import initializeMap from "../components/venue_map";
import initOfferEnquiries from "../components/enquiries/offer_enquiry";
import initReviews from "../components/venue_reviews";
import initVenueEnquiry, { setLayout as setVenueEnquiryLayout } from "../components/enquiries/venue_enquiry";
import lazyLoadMap from "../shared/helpers/map_loader";
import OffersList from "../components/last_minute_deals/OffersList";
import UserTiming from "../shared/helpers/user-timing";

// from common.js
import autocompleteToggle from "../components/autocomplete/AutocompleteToggle.js";
import embedYoutube from "../components/video.js";
import initGeneralEnquiry from "../components/enquiries/general_enquiry";
import initNamedEnquiry from "../components/enquiries/named_enquiry";
import scrollspy from "../components/scrollspy/scrollspy.js";

import mountNewsletterForm from "../controllers/Newsletter.jsx";
import mountPopovers from "../controllers/Popover.jsx";

import ellipsis from "../shared/helpers/ellipsis";
import emarsys from "../shared/helpers/emarsys";
import forceImageLoad from "../shared/helpers/defer-picture";
import notifier from "../shared/helpers/airbrake";

import "../shared/helpers/responsive-bg";
import '../shared/gtm-script-loader';
import '../shared/notifications';

import "./common.scss";
// end from common.js

import "../scripts/sections/blocks/autocomplete";
import "../components/shared/video_gallery";

import "./venues.scss";

const userTiming = new UserTiming({ src: "venues" });

const DEBOUNCE_DELAY = 100;

const { ONLINE_BOOKING_HOST } = window;

function handleReadMore(e) {
  e.preventDefault();

  document.getElementById("js-read-more-content").removeAttribute("style");
  document.getElementById("reviews").classList.remove("is-collapsed");
}

function initReadMore() {
  const readMore = document.getElementById("js-read-more");

  if (readMore) {
    readMore.addEventListener("click", handleReadMore);
  }
}

const initStripe = () => {
  window.stripeDialog = function () {
    if (!window.stripeAdapter) {
      window.stripeAdapter = StripeCheckout.configure({
        key: window.stripeKey,
        name: "Your Golf Travel Limited",
        image: "https://www.yourgolftravel.com/favicon.ico",
        locale: "auto",
        currency: "gbp",
        allowRememberMe: false
      });
    }

    return window.stripeAdapter;
  };

  window.addEventListener("popstate", () => window.stripeDialog().close());
};

function setReviewsHeight() {
  const articles = document.querySelectorAll("#js-read-more-content article");
  const { length: articlesCount } = articles;
  const maxArticles = Math.min(5, articlesCount);
  const readMoreContent = document.getElementById("js-read-more-content");
  let reviewHeight = 0;

  if (readMoreContent) {
    [].slice.call(articles, 0, maxArticles).forEach(article => {
      reviewHeight += article.offsetHeight;
    });

    readMoreContent.style.height = `${reviewHeight}px`;

    if (articlesCount > maxArticles) {
      document.getElementById("reviews").classList.add("is-collapsed");
    }
  }
}

function loading(state) {
  const jsLoading = document.getElementById("js-loading");

  if (jsLoading) {
    jsLoading.classList.toggle("is-visible", state);
  }
}

function getVenueParams(date) {
  const { pathname } = window.location;
  const venueSlug = pathname.substr(pathname.indexOf("/") + 1);

  return {
    venue_slug: venueSlug,
    package_id: getParamByName("package"),
    arrival_date: (date ? date.format(ISO_DATE_FORMAT) : null)
  };
}

function toggleBookButton(date) {
  document.querySelectorAll(".js-booking-form").forEach(form => {
    form.classList.toggle("hide", date === null);
  });
}

const renderLastMinuteDeals = () => {
  const container = document.getElementById("js-venue-tours-lmd-container");

  if (container) {
    const { dataset } = container;
    const offers = document.getElementById("json-lmd-offers").textContent;

    ReactDOM.render(
      <OffersList
        consentStatement={dataset.consentStatement}
        offers={JSON.parse(offers)}
        venueName={dataset.venueName}
        selectLabel="Book now"
        hideNoOffersMessage
        isVenuePage
      />,
      container
    );
  }
};

function resizeOffersContainer() {
  const offers = document.querySelector("#offers");
  const { clientHeight } = offers;

  let container = document.querySelector("#js-venue-enquiry-container");
  if (!container) {
    return;
  }

  container = container.nextSibling;
  while (container && container.nodeType !== 1) { // NodeType.ELEMENT
    container = container.nextSibling;
  }

  if (container) {
    offers.style.marginBottom = `${clientHeight * -1}px`;
    container.style.marginTop = `${clientHeight}px`;
  }
}

function updateOffersCount() {
  const count = document.getElementById("offers").querySelectorAll(".venue__offer:not([hidden])").length;
  const innerCount = `${pluralize("offer", count, true)} that ${count === 1 ? "matches" : "match"}`;

  document.getElementById("js-offers-count").innerText = `We found ${innerCount} your search`;
}

function initOfferTabs() {
  const control = document.querySelector(".offers__tabs");

  if (control) {
    control.addEventListener("click", ({ target }) => {
      if (!target.closest(".active")) {
        control.querySelectorAll("li").forEach(li => {
          const { textContent } = li.querySelector("a");

          li.classList.toggle("active", target.textContent === textContent);
        });

        const all = target.dataset.tab === "all";
        const fn = all ? "removeAttribute" : "setAttribute";

        document.querySelector("[data-featured-offer]")[fn]("hidden", "");
        document.querySelector("#js-offers-list")
          .querySelectorAll(".venue__offer")
          .forEach(offer => {
            offer[fn]("hidden", "");
          });
      }

      updateOffersCount();
      resizeOffersContainer();
    });
  }
}

function setIframeSrc(iframe, { arrivalDate, packageId }) {
  const searchParams = new URLSearchParams();

  if (arrivalDate) {
    searchParams.append("arrival_date", arrivalDate);
  }

  if (packageId) {
    searchParams.append("package_id", packageId);
  }

  const url = new URL(`${ONLINE_BOOKING_HOST}/booking`);

  url.search = searchParams.toString();
  iframe.src = url.toString();
}

function initOnlineBooking() {
  const maxWidth = "639px";

  $("#online-booking-modal").on("show.bs.modal", (e) => {
    const iframe = e.target.querySelector('iframe');
    const { arrivalDate, packageId } = e.relatedTarget.dataset;

    try {
      const lastOpenedPackage = localStorage.getItem('onlineBookingPackageId');

      if (iframe.src === '' || packageId !== lastOpenedPackage) {
        localStorage.setItem('onlineBookingPackageId', packageId);
      }
      // eslint-disable-next-line no-empty
    } catch (_) {}

    setIframeSrc(iframe, { arrivalDate, packageId });
  });

  window.addEventListener('message', (e) => {
    if (!e.data) {
      return;
    }

    const onlineBookingModal = document.getElementById("online-booking-modal");

    switch (e.data.eventType) {
      case "onlineBookingModalExit":
        if (onlineBookingModal) {
          $(onlineBookingModal).modal("hide");
          onlineBookingModal.querySelector(".modal-dialog").style.maxWidth = maxWidth;
        }

        break;

      case "onlineBookingNavigateToHome": {
        window.location.href = "/";

        break;
      }

      case "onlineBookingNavigateToRooms": {
        if (onlineBookingModal) {
          onlineBookingModal.querySelector(".modal-dialog").style.maxWidth = '100%';
        }

        break;
      }

      case "onlineBookingPushToDatalayer": {
        pushGa4Event(e.data.event);

        break;
      }

      case "onlineBookingURLQuery": {
        const iframe = onlineBookingModal.querySelector('iframe');
        if (iframe) {
          iframe.contentWindow.postMessage({
            eventType: 'onlineBookingURLQuery',
            url: window.location.href,
            entryUrl: window.sessionStorage && sessionStorage.getItem("entryURL"),
          }, ONLINE_BOOKING_HOST)
        }

        break;
      }

      default:
    }
  });
}

function getPackages(date) {
  loading(true);

  const venueParams = getVenueParams(date);

  venueParams.offers_only = true;

  json(venueParamsToUrl(venueParams))
    .then(data => {
      const featuredOffer = document.querySelector("[data-featured-offer]");

      if (featuredOffer) {
        featuredOffer.parentNode.removeChild(featuredOffer);
      }

      const { no_offers: noOffers } = data;

      document.getElementById("js-offers-count").outerHTML = data.offers_count;
      document.getElementById("js-offers-list").outerHTML = data.offers_list;
      document.getElementById("js-no-offers-bumper").outerHTML = data.no_offers;
      document.getElementById("js-venue-tours-lmd-container").outerHTML = data.last_minute_deals;
      toggleBookButton(date);
      loading(false);
      initOfferEnquiries();
      setVenueEnquiryLayout();
      renderLastMinuteDeals(); // FIXME: LMD where this should go?
      const tab = document.querySelector(`.offers__tabs [data-tab=all]`);

      if (tab) {
        tab.dispatchEvent(new MouseEvent("click", { bubbles: true })); // trigger tab change (if necessary)
      }

      if (noOffers.indexOf("callout-box") !== -1) {
        gaEventNoHit("Venue Page", "No results", location.pathname + location.search);
      }
    })
    .catch(() => loading(false));
}

function setURL(date) {
  const venueParams = getVenueParams(date);

  window.history.replaceState({}, "", venueParamsToUrl(venueParams));
}

function setAllDatepickers(date) {
  const dateValue = date ? date.format(UK_DATE_FORMAT) : null;
  const dateInputs = [
    ...document.querySelectorAll(".js-arrival-date"),
    ...document.querySelectorAll(".js-offer-date")
  ];

  dateInputs.forEach(input => { input.value = dateValue; });
}

// Date is optional
function setCookie(date) {
  const value = date ? date.format(UK_DATE_FORMAT) : '';
  Cookies.remove("arrival_date");

  if (value) {
    Cookies.set("arrival_date", value, { samesite: "lax", secure: true });
  }
}

function handleDateChange(date, initialLoad) {
  if (initialLoad && !date) {
    setTimeout(setVenueEnquiryLayout, 1000);
  } else {
    getPackages(date);
  }

  setURL(date);
  setAllDatepickers(date);
  setCookie(date);
}

const checkDate = (date, now) => date && date.isValid() && date.isAfter(now);

function getPreselectedDate() {
  const now = dayjs();
  const paramDate = dayjs(getParamByName("arrival_date"), ISO_DATE_FORMAT);
  const cookieDate = dayjs(Cookies.get("arrival_date"), UK_DATE_FORMAT);

  return [paramDate, cookieDate].find(date => checkDate(date, now)) || null;
}

function setInitialDate() {
  handleDateChange(getPreselectedDate(), true);
}

function initArrivalDates() {
  document.querySelectorAll(".js-arrival-date")
    .forEach((el) => {
      const container = el.closest("#js-venue-enquiry-form");

      $(el).pickadate(regularPickerOptions(container, handleDateChange))
        .pickadate("picker");
    });

  setInitialDate();
}

function stopVideos() {
  document.querySelectorAll(".js-video").forEach(video => {
    const src = video.getAttribute("src");

    video.setAttribute("src", "");
    video.setAttribute("src", src);
  });
}

function initPopovers() {
  const $popovers = $("[data-toggle='popover']");

  $popovers.popover();
  window.addEventListener("scroll", debounce(() => {
    $popovers.popover("hide").blur();
  }, DEBOUNCE_DELAY));
}

function scrollTo(target) {
  if (target) {
    const { y } = target.getBoundingClientRect();
    const { scrollTop } = document.documentElement;

    window.scrollTo(0, (y + scrollTop) - 64);
  }
}

function initTopOfferJumpLinks() {
  function scroller(e) {
    e.preventDefault();
    const tab = document.querySelector(`.offers__tabs [data-tab=${this.dataset.tab}]`);

    tab.dispatchEvent(new MouseEvent("click", { bubbles: true })); // trigger tab change (if necessary)
    scrollTo(document.querySelector(`.offers__tabs`));
  }

  const container = document.querySelector(".venue__offer--top-offers");

  if (container) {
    container.querySelectorAll(".venue__offer>a").forEach(link => {
      link.addEventListener("click", scroller.bind(link));
    });
  }
}

function trackHeroAnchorClicks() {
  const venueHeroBanner = document.getElementById("js-venue-hero");

  if (venueHeroBanner) {
    venueHeroBanner.addEventListener("click", e => {
      const target = e.target.closest("a");

      if (target) {
        e.stopImmediatePropagation();

        trackClick(target);
      }
    });
  }
}

// from common.js
function handleClickTracking() {
  document.addEventListener("click", e => {
    const target = e.target.closest(".gtm-track-click");

    if (target) {
      trackClick(target);
    }
  });
}
// end from common.js

function onDOMContentLoaded() {
  userTiming.start("onDOMContentLoaded");

  userTiming.start("common");
  // from common.js
  autocompleteToggle();
  initGeneralEnquiry();
  initNamedEnquiry();
  handleClickTracking();
  ellipsis();
  embedYoutube();
  mountNewsletterForm();
  mountPopovers();
  scrollspy();

  // end from common.js
  userTiming.finish("common");

  // Enquiry forms
  initVenueEnquiry();
  initOfferEnquiries();

  // Images
  forceImageLoad(document.querySelector(".carousel-indicators"));

  // Prevent triggering modal on regular link clicks
  trackHeroAnchorClicks();

  // Stop videos on modal close
  $("#js-venue-gallery").on("hide.bs.modal", stopVideos);

  // Arrival date filters
  initArrivalDates();

  // Map
  lazyLoadMap(initializeMap);

  // Other
  renderLastMinuteDeals();
  initStripe();
  initReadMore();
  setReviewsHeight();
  initPopovers();
  trackGoal("VIEWED_VENUE");
  initTopOfferJumpLinks();
  initOfferTabs();
  initReviews();
  initOnlineBooking();

  userTiming.finish("onDOMContentLoaded");
}

document.addEventListener("DOMContentLoaded", onDOMContentLoaded);

// from common.js
window.addEventListener("error", error => {
  notifier.notify({ error });
});

emarsys(window.emarsysData);
// end from common.js

userTiming.finish();



// WEBPACK FOOTER //
// ./entry_points/venues.js